import { logError } from '@hotjar/insights-common/lib/logger';
import type {
  LargeListSingleFilterState,
  FilterType,
  ExperimentSingleFilterState,
  SingleFilterState,
  UserAttributeSingleFilterState,
  FunnelFilter,
} from '../../domains/global-filters/model/FilterChain';
import { FilterKindEnum } from '../../domains/global-filters/model/FilterChain';
import { BrowserFilter } from './SingleFilter/BrowserFilter/BrowserFilter';
import { CountryFilter } from './SingleFilter/CountryFilter/CountryFilter';
import { DeviceTypeFilter } from './SingleFilter/DeviceTypeFilter/DeviceTypeFilter';
import { EventFilter } from './SingleFilter/EventFilter/EventFilter';
import { FeedbackFilter } from './SingleFilter/FeedbackFilter/FeedbackFilter';
import { FilterTag } from './SingleFilter/GenericBloc/GenericFilter/FilterTag';
import { KeywordFilter } from './SingleFilter/KeywordFilter/KeywordFilter';
import { NewReturningUserFilter } from './SingleFilter/NewReturningUserFilter/NewReturningUserFilter';
import { NPSFilter } from './SingleFilter/NPSFilter/NPSFilter';
import { OperatingSystemFilter } from './SingleFilter/OperatingSystemFilter/OperatingSystemFilter';
import { PageCountFilter } from './SingleFilter/PageCountFilter/PageCountFilter';
import { RageClickFilter } from './SingleFilter/RageClickFilter/RageClickFilter';
import { SessionDurationFilter } from './SingleFilter/SessionDurationFilter/SessionDurationFilter';
import { ExitURLFilter } from './SingleFilter/URLFilter/ExitURLFilter/ExitURLFilter';
import { LandingURLFilter } from './SingleFilter/URLFilter/LandingURLFilter/LandingURLFilter';
import { PageURLFilter } from './SingleFilter/URLFilter/PageURLFilter/PageURLFilter';
import { ReferrerURLFilter } from './SingleFilter/URLFilter/ReferrerURLFilter/ReferrerURLFilter';
import { VisitedURLFilter } from './SingleFilter/URLFilter/VisitedURLFilter/VisitedURLFilter';
import { UserIdFilter } from './SingleFilter/UserIdFilter/UserIdFilter';
import { UTurnFilter } from './SingleFilter/UTurnFilter/UTurnFilter';
import { UserAttributeFilter } from './SingleFilter/UserAttributeFilter/UserAttributeFilter';
import { SurveyCompletionFilter } from './SingleFilter/SurveyCompletionFilter/SurveyCompletionFilter';
import { TeamCommentFilter } from './SingleFilter/TeamCommentFilter/TeamCommentFilter';
import { GoogleOptimizeFilter } from './SingleFilter/ExperimentFilter/GoogleOptimizeFilter/GoogleOptimizeFilter';
import { OptimizelyFilter } from './SingleFilter/ExperimentFilter/OptimizelyFilter/OptimizelyFilter';
import { UnbounceFilter } from './SingleFilter/ExperimentFilter/UnbounceFilter/UnbounceFilter';
import { ClickedFilter } from './SingleFilter/ClickedFilter/ClickedFilter';
import type { DateRange } from './SingleFilter/DateRangeFilter/DateRangeFilterType';
import { ErrorFilter } from './SingleFilter/ErrorFilter/ErrorFilter';

const showUnknownPills = (never: never | FunnelFilter, onRemove: () => void): JSX.Element => {
  logError(new Error(`Unknown Single Filter kind (We shouldn't reach this part): ${never}`));
  return <FilterTag filterName="Unknown filter" iconName="warning" onRemove={onRemove} />;
};

interface SingleFilterWrapperProps {
  currentValue: FilterType;
  autoFocus?: boolean;
  onApply: (newValue: FilterType) => void;
  onRemove: () => void;
  onDropdownHidden?: () => void;
  showDropdownOnMount?: boolean;
  filterStateByKind: Map<FilterKindEnum, SingleFilterState>;
  dateRange?: DateRange;
  isFunnelStep?: boolean;
  isScaleTrial?: boolean;
}

export function SingleFilterWrapper({
  currentValue,
  autoFocus,
  onApply,
  onRemove,
  onDropdownHidden,
  showDropdownOnMount,
  filterStateByKind,
  dateRange,
  isFunnelStep,
  isScaleTrial,
}: SingleFilterWrapperProps): JSX.Element | null {
  switch (currentValue.kind) {
    case FilterKindEnum.Clicked: {
      const filterState = filterStateByKind.get(FilterKindEnum.Clicked);
      if (filterState == null) {
        throw new Error(`No FilterState for kind ${FilterKindEnum.Clicked}`);
      }
      return (
        <ClickedFilter
          autoFocus={autoFocus}
          currentValue={currentValue.value}
          onApply={(newValue) =>
            onApply({
              ...currentValue,
              value: newValue,
            })
          }
          onRemove={onRemove}
          onDropdownHidden={onDropdownHidden}
          showDropdownOnMount={showDropdownOnMount}
          dateConstraint={filterState.config.dateConstraint}
          dateRange={dateRange}
          isFunnelStep={isFunnelStep}
        />
      );
    }
    case FilterKindEnum.Error: {
      const filterState = filterStateByKind.get(FilterKindEnum.Error);
      if (filterState == null) {
        throw new Error(`No FilterState for kind ${FilterKindEnum.Error}`);
      }
      return (
        <ErrorFilter
          autoFocus={autoFocus}
          currentValue={currentValue.value}
          onApply={(newValue) =>
            onApply({
              ...currentValue,
              value: newValue,
            })
          }
          onRemove={onRemove}
          onDropdownHidden={onDropdownHidden}
          showDropdownOnMount={showDropdownOnMount}
          dateConstraint={filterState.config.dateConstraint}
          dateRange={dateRange}
          isScaleTrial={isScaleTrial}
        />
      );
    }
    case FilterKindEnum.NewReturningUserKind:
      return (
        <NewReturningUserFilter
          autoFocus={autoFocus}
          currentValue={currentValue.value}
          onApply={(newValue) =>
            onApply({
              ...currentValue,
              value: newValue,
            })
          }
          onRemove={onRemove}
          onDropdownHidden={onDropdownHidden}
          showDropdownOnMount={showDropdownOnMount}
        />
      );
    case FilterKindEnum.RageClickKind:
      return (
        <RageClickFilter
          autoFocus={autoFocus}
          currentValue={currentValue.value}
          onApply={(newValue) =>
            onApply({
              ...currentValue,
              value: newValue,
            })
          }
          onRemove={onRemove}
          onDropdownHidden={onDropdownHidden}
          showDropdownOnMount={showDropdownOnMount}
        />
      );
    case FilterKindEnum.UTurnKind:
      return (
        <UTurnFilter
          autoFocus={autoFocus}
          currentValue={currentValue.value}
          onApply={(newValue) =>
            onApply({
              ...currentValue,
              value: newValue,
            })
          }
          onRemove={onRemove}
          onDropdownHidden={onDropdownHidden}
          showDropdownOnMount={showDropdownOnMount}
        />
      );
    case FilterKindEnum.TeamComment:
      return (
        <TeamCommentFilter
          autoFocus={autoFocus}
          currentValue={currentValue.value}
          onApply={(newValue) =>
            onApply({
              ...currentValue,
              value: newValue,
            })
          }
          onRemove={onRemove}
          onDropdownHidden={onDropdownHidden}
          showDropdownOnMount={showDropdownOnMount}
        />
      );
    case FilterKindEnum.DeviceType:
      return (
        <DeviceTypeFilter
          autoFocus={autoFocus}
          currentValue={currentValue.value}
          onApply={(newValue) =>
            onApply({
              ...currentValue,
              value: newValue,
            })
          }
          onRemove={onRemove}
          onDropdownHidden={onDropdownHidden}
          showDropdownOnMount={showDropdownOnMount}
        />
      );
    case FilterKindEnum.OS:
      return (
        <OperatingSystemFilter
          autoFocus={autoFocus}
          currentValue={currentValue.value}
          onApply={(newValue) =>
            onApply({
              ...currentValue,
              value: newValue,
            })
          }
          onRemove={onRemove}
          onDropdownHidden={onDropdownHidden}
          showDropdownOnMount={showDropdownOnMount}
        />
      );
    case FilterKindEnum.Country: {
      const filterState = filterStateByKind.get(
        FilterKindEnum.Country,
      ) as LargeListSingleFilterState;
      if (filterState == null) {
        throw new Error(`No FilterState for kind ${FilterKindEnum.Country}`);
      }
      return (
        <CountryFilter
          autoFocus={autoFocus}
          currentValue={currentValue.value}
          onApply={(newValue) =>
            onApply({
              ...currentValue,
              value: newValue,
            })
          }
          onRemove={onRemove}
          onDropdownHidden={onDropdownHidden}
          showDropdownOnMount={showDropdownOnMount}
          optionList={filterState.config.optionList}
        />
      );
    }
    case FilterKindEnum.Event: {
      const filterState = filterStateByKind.get(FilterKindEnum.Event) as LargeListSingleFilterState;
      if (filterState == null) {
        throw new Error(`No FilterState for kind ${FilterKindEnum.Event}`);
      }
      return (
        <EventFilter
          autoFocus={autoFocus}
          currentValue={currentValue.value}
          onApply={(newValue) =>
            onApply({
              ...currentValue,
              value: newValue,
            })
          }
          onRemove={onRemove}
          onDropdownHidden={onDropdownHidden}
          showDropdownOnMount={showDropdownOnMount}
          optionList={filterState.config.optionList}
          optionListIsLoading={filterState.config.optionListIsLoading}
          onDropdownShown={filterState.config.onDropdownShown}
          isFunnelStep={isFunnelStep}
        />
      );
    }
    case FilterKindEnum.GoogleOptimize: {
      const filterState = filterStateByKind.get(
        FilterKindEnum.GoogleOptimize,
      ) as ExperimentSingleFilterState;
      if (filterState == null) {
        throw new Error(`No FilterState for kind ${FilterKindEnum.GoogleOptimize}`);
      }
      return (
        <GoogleOptimizeFilter
          autoFocus={autoFocus}
          currentValue={currentValue.value}
          onApply={(newValue) =>
            onApply({
              ...currentValue,
              value: newValue,
            })
          }
          onRemove={onRemove}
          onDropdownHidden={onDropdownHidden}
          showDropdownOnMount={showDropdownOnMount}
          optionList={filterState.config.optionList}
          optionListIsLoading={filterState.config.optionListIsLoading}
          onDropdownShown={filterState.config.onDropdownShown}
          isIntegrationActive={filterState.config.isIntegrationActive}
        />
      );
    }
    case FilterKindEnum.Optimizely: {
      const filterState = filterStateByKind.get(
        FilterKindEnum.Optimizely,
      ) as ExperimentSingleFilterState;
      if (filterState == null) {
        throw new Error(`No FilterState for kind ${FilterKindEnum.Optimizely}`);
      }
      return (
        <OptimizelyFilter
          autoFocus={autoFocus}
          currentValue={currentValue.value}
          onApply={(newValue) =>
            onApply({
              ...currentValue,
              value: newValue,
            })
          }
          onRemove={onRemove}
          onDropdownHidden={onDropdownHidden}
          showDropdownOnMount={showDropdownOnMount}
          optionList={filterState.config.optionList}
          optionListIsLoading={filterState.config.optionListIsLoading}
          onDropdownShown={filterState.config.onDropdownShown}
          isIntegrationActive={filterState.config.isIntegrationActive}
        />
      );
    }
    case FilterKindEnum.Unbounce: {
      const filterState = filterStateByKind.get(
        FilterKindEnum.Unbounce,
      ) as ExperimentSingleFilterState;
      if (filterState == null) {
        throw new Error(`No FilterState for kind ${FilterKindEnum.Unbounce}`);
      }
      return (
        <UnbounceFilter
          autoFocus={autoFocus}
          currentValue={currentValue.value}
          onApply={(newValue) =>
            onApply({
              ...currentValue,
              value: newValue,
            })
          }
          onRemove={onRemove}
          onDropdownHidden={onDropdownHidden}
          showDropdownOnMount={showDropdownOnMount}
          optionList={filterState.config.optionList}
          optionListIsLoading={filterState.config.optionListIsLoading}
          onDropdownShown={filterState.config.onDropdownShown}
          isIntegrationActive={filterState.config.isIntegrationActive}
        />
      );
    }
    case FilterKindEnum.VisitedURL:
      return (
        <VisitedURLFilter
          autoFocus={autoFocus}
          currentValue={currentValue.value}
          onApply={(newValue) =>
            onApply({
              ...currentValue,
              value: newValue,
            })
          }
          onRemove={onRemove}
          onDropdownHidden={onDropdownHidden}
          showDropdownOnMount={showDropdownOnMount}
          isFunnelStep={isFunnelStep}
        />
      );
    case FilterKindEnum.LandingURL:
      return (
        <LandingURLFilter
          autoFocus={autoFocus}
          currentValue={currentValue.value}
          onApply={(newValue) =>
            onApply({
              ...currentValue,
              value: newValue,
            })
          }
          onRemove={onRemove}
          onDropdownHidden={onDropdownHidden}
          showDropdownOnMount={showDropdownOnMount}
        />
      );
    case FilterKindEnum.ExitURL:
      return (
        <ExitURLFilter
          autoFocus={autoFocus}
          currentValue={currentValue.value}
          onApply={(newValue) =>
            onApply({
              ...currentValue,
              value: newValue,
            })
          }
          onRemove={onRemove}
          onDropdownHidden={onDropdownHidden}
          showDropdownOnMount={showDropdownOnMount}
        />
      );
    case FilterKindEnum.ReferrerURL:
      return (
        <ReferrerURLFilter
          autoFocus={autoFocus}
          currentValue={currentValue.value}
          onApply={(newValue) =>
            onApply({
              ...currentValue,
              value: newValue,
            })
          }
          onRemove={onRemove}
          onDropdownHidden={onDropdownHidden}
          showDropdownOnMount={showDropdownOnMount}
        />
      );
    case FilterKindEnum.PageURL:
      return (
        <PageURLFilter
          autoFocus={autoFocus}
          currentValue={currentValue.value}
          onApply={(newValue) =>
            onApply({
              ...currentValue,
              value: newValue,
            })
          }
          onRemove={onRemove}
          onDropdownHidden={onDropdownHidden}
          showDropdownOnMount={showDropdownOnMount}
        />
      );
    case FilterKindEnum.Browser:
      return (
        <BrowserFilter
          autoFocus={autoFocus}
          currentValue={currentValue.value}
          onApply={(newValue) =>
            onApply({
              ...currentValue,
              value: newValue,
            })
          }
          onRemove={onRemove}
          onDropdownHidden={onDropdownHidden}
          showDropdownOnMount={showDropdownOnMount}
        />
      );
    case FilterKindEnum.IncomingFeedback:
      return (
        <FeedbackFilter
          autoFocus={autoFocus}
          currentValue={currentValue.value}
          onApply={(newValue) =>
            onApply({
              ...currentValue,
              value: newValue,
            })
          }
          onRemove={onRemove}
          onDropdownHidden={onDropdownHidden}
          showDropdownOnMount={showDropdownOnMount}
        />
      );
    case FilterKindEnum.SurveyCompletionKind:
      return (
        <SurveyCompletionFilter
          currentValue={currentValue.value}
          onApply={(newValue) =>
            onApply({
              ...currentValue,
              value: newValue,
            })
          }
          onRemove={onRemove}
          onDropdownHidden={onDropdownHidden}
          showDropdownOnMount={showDropdownOnMount}
        />
      );
    case FilterKindEnum.NPS:
      return (
        <NPSFilter
          autoFocus={autoFocus}
          currentValue={currentValue.value}
          onApply={(newValue) =>
            onApply({
              ...currentValue,
              value: newValue,
            })
          }
          onRemove={onRemove}
          onDropdownHidden={onDropdownHidden}
          showDropdownOnMount={showDropdownOnMount}
        />
      );
    case FilterKindEnum.UserAttribute: {
      const filterState = filterStateByKind.get(
        FilterKindEnum.UserAttribute,
      ) as UserAttributeSingleFilterState;
      if (filterState == null) {
        throw new Error(`No FilterState for kind ${FilterKindEnum.UserAttribute}`);
      }
      return (
        <UserAttributeFilter
          autoFocus={autoFocus}
          currentValue={currentValue.value}
          onApply={(newValue) =>
            onApply({
              ...currentValue,
              value: newValue,
            })
          }
          onRemove={onRemove}
          onDropdownHidden={onDropdownHidden}
          showDropdownOnMount={showDropdownOnMount}
          userAttributeOptionList={filterState.config.userAttributeOptionList}
          userAttributeOptionIsLoading={filterState.config.userAttributeOptionIsLoading}
          onDropdownShown={filterState.config.onDropdownShown}
          onAddMoreUserAttribute={filterState.config.onAddMoreUserAttribute}
        />
      );
    }
    case FilterKindEnum.UserID:
      return (
        <UserIdFilter
          autoFocus={autoFocus}
          currentValue={currentValue.value}
          onApply={(newValue) =>
            onApply({
              ...currentValue,
              value: newValue,
            })
          }
          onRemove={onRemove}
          onDropdownHidden={onDropdownHidden}
          showDropdownOnMount={showDropdownOnMount}
        />
      );
    case FilterKindEnum.Keyword:
      return (
        <KeywordFilter
          autoFocus={autoFocus}
          currentValue={currentValue.value}
          onApply={(newValue) =>
            onApply({
              ...currentValue,
              value: newValue,
            })
          }
          onRemove={onRemove}
          onDropdownHidden={onDropdownHidden}
          showDropdownOnMount={showDropdownOnMount}
        />
      );
    case FilterKindEnum.PageCount:
      return (
        <PageCountFilter
          autoFocus={autoFocus}
          currentValue={currentValue.value}
          onApply={(newValue) =>
            onApply({
              ...currentValue,
              value: newValue,
            })
          }
          onRemove={onRemove}
          onDropdownHidden={onDropdownHidden}
          showDropdownOnMount={showDropdownOnMount}
        />
      );
    case FilterKindEnum.SessionDuration:
      return (
        <SessionDurationFilter
          autoFocus={autoFocus}
          currentValue={currentValue.value}
          onApply={(newValue) =>
            onApply({
              ...currentValue,
              value: newValue,
            })
          }
          onRemove={onRemove}
          onDropdownHidden={onDropdownHidden}
          showDropdownOnMount={showDropdownOnMount}
        />
      );
    default:
      return showUnknownPills(currentValue, onRemove);
  }
}
